/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

export type StepperProps = {
  active: number;
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  orientation?: 'horizontal' | 'vertical';
  children: React.ReactNode;
  className?: string;
  completedIcon?: React.ReactNode;
  classNames?: Record<string, string>;
};

export type StepperStepProps = {
  label?: string;
  description?: string;
  icon?: React.ReactNode;
  completed?: boolean;
  active?: boolean;
  children?: React.ReactNode;
  _classNames?: Record<string, string>;
  _completedIcon?: React.ReactNode;
  _isLast?: boolean;
};

const StepperBase: React.FC<StepperProps> = ({
  active,
  children,
  className,
  classNames,
  completedIcon,
}) => {
  const childArray = React.Children.toArray(children);
  const totalSteps = childArray.length;

  return (
    <div className={cn('stepper-root font-(--font-family) w-full', classNames?.root, className)}>
      <div
        className={cn(
          'stepper-steps flex items-start w-full justify-evenly gap-0',
        )}
      >
        {childArray.map((child, index) => {
          if (React.isValidElement<StepperStepProps>(child)) {
            const isActive = index === active;
            const isCompleted = index < active;
            const isLast = index === totalSteps - 1;

            return React.cloneElement(child, {
              key: index,
              active: isActive,
              completed: isCompleted,
              _classNames: classNames,
              _completedIcon: completedIcon,
              _isLast: isLast,
            });
          }
          return child;
        })}
      </div>
    </div>
  );
};

export const StepperStep: React.FC<StepperStepProps> = ({
  label,
  description,
  icon,
  completed,
  active,
  children,
  _classNames: classNames,
  _completedIcon: completedIcon,
}) => {
  const iconContent = completed && completedIcon ? completedIcon : icon;

  return (
    <div
      className={cn(
        'stepper-step flex-1 flex flex-col items-center justify-start relative m-0 px-1 min-w-0',
        classNames?.step,
      )}
    >
      <div
        className={cn(
          'stepper-step-icon relative z-1',
          'w-10 h-10 rounded-full flex items-center justify-center',
          'border-2 transition-all duration-300',
          'max-lg:w-7 max-lg:h-7',
          !completed && !active && 'border-(--border-subtle) bg-(--surface-primary) text-(--text-tertiary) shadow-[var(--shadow-sm)]',
          completed && 'border-(--button-primary-bg) bg-(--button-primary-bg) text-(--button-primary-text) shadow-[0_2px_6px_rgb(var(--seed-rgb)/0.2)]',
          active && 'border-(--button-primary-bg) bg-(--button-primary-bg) text-(--button-primary-text) shadow-[0_0_0_4px_rgb(var(--seed-rgb)/0.15)] max-lg:shadow-[0_0_0_3px_rgb(var(--seed-rgb)/0.15)]',
          classNames?.stepIcon,
        )}
        data-completed={completed ? '' : undefined}
        data-progress={active ? '' : undefined}
      >
        {iconContent || (
          <svg className="w-5 h-5 max-lg:w-3.5 max-lg:h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      {(label || description) && (
        <div
          className={cn(
            'stepper-step-body m-0 mt-2 p-0 text-center w-full max-lg:mt-1',
            classNames?.stepBody,
          )}
        >
          {label && (
            <div
              className={cn(
                'stepper-step-label',
                'font-(--font-family) text-body font-(--font-medium) text-(--text-primary)',
                'leading-(--leading-snug) m-0 mt-1 p-0 text-center w-full transition-colors duration-300',
                'max-lg:text-[0.625rem] max-lg:min-h-[2em] max-lg:line-clamp-2 max-lg:mt-0.5',
                classNames?.stepLabel,
              )}
            >
              {label}
            </div>
          )}
          {description && (
            <div className={cn(
              'font-(--font-family) text-small text-(--text-secondary) mt-1 leading-(--leading-snug)',
              classNames?.stepDescription,
            )}>
              {description}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

type StepperCompound = React.FC<StepperProps> & {
  Step: React.FC<StepperStepProps>;
};

export const Stepper: StepperCompound = Object.assign(StepperBase, {
  Step: StepperStep,
});

export default Stepper;
